import { useRef, useEffect } from "react";
import { animate } from "framer-motion";

/**
 * Scroll-into-view reveal — Framer Motion, modeled on the Duskera reference.
 *
 * EACH element is observed individually and reveals only when IT scrolls into
 * view (~15% in), then plays a self-driven fade + rise on its own clock — NOT
 * tied to scroll position (so never sluggish), and NOT tied to the wrapper (so
 * a card in row 2 of a grid never animates while it's still below the fold).
 * Smooth easeOutCubic, 0.6s.
 *
 *   • Block reveal (default): the wrapper itself fades + rises as it enters.
 *   • `.stagger-item` descendants each fade + rise as they enter (rows cascade
 *     naturally as you scroll). `.stagger-clip` images wipe open as they enter.
 *
 * `prefers-reduced-motion` is handled in CSS (base rules flip to visible); we
 * skip animating for those users. Client-only SPA → no no-JS guard.
 *
 * - `as` renders the wrapper as a different element (default <div>).
 * - `delay` (seconds) offsets a block reveal's start.
 */

const EASE = [0.22, 1, 0.36, 1]; // easeOutCubic — the reference site's curve
const RISE = { duration: 0.6, ease: EASE };
const CLIP = { duration: 0.8, ease: EASE };
// Fire when the element itself is ~25% into the viewport, so the motion plays
// as you reach it — not when the section's top edge first peeks in.
const ROOT_MARGIN = "0px 0px -25% 0px";

function Reveal({ children, as: Tag = "div", className = "", delay = 0, ...rest }) {
	const ref = useRef(null);
	const isStagger = className.split(" ").includes("reveal--stagger");

	useEffect(() => {
		const node = ref.current;
		if (!node) return;
		// Reduced-motion users get the rest state from CSS; don't animate.
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

		// Observe the .stagger-clip WRAPPER, never the clipped <img> itself: a
		// fully clipped element has an empty visible rect, so its intersection
		// ratio is pinned at 0 — the observer below would never fire for it, and
		// Chrome's native lazy-loader would never fetch it (same geometry rules).
		const clipWraps = new Set(node.querySelectorAll(".stagger-clip"));
		const items = new Set(node.querySelectorAll(".stagger-item"));
		const targets = [...items, ...clipWraps];
		// A block (non-stagger) reveal fades the wrapper itself as one unit.
		if (!isStagger) targets.push(node);
		if (!targets.length) return;

		const reveal = (el) => {
			if (clipWraps.has(el)) {
				const img = el.querySelector("img");
				if (!img) return;
				// A lazy image hidden by clip-path never loads on its own (zero
				// visible area), and decode() would wait forever on it — switch
				// to eager to start the load, then wipe once pixels are ready.
				img.loading = "eager";
				img.decode()
					.catch(() => {})
					.then(() =>
						animate(img, { clipPath: ["inset(0 0 100% 0)", "inset(0 0 0% 0)"] }, CLIP)
					);
			} else {
				const y = items.has(el) ? 24 : 20;
				animate(el, { opacity: [0, 1], y: [y, 0] }, { ...RISE, delay });
			}
		};

		const io = new IntersectionObserver(
			(entries) => {
				for (const e of entries) {
					if (e.isIntersecting) {
						reveal(e.target);
						io.unobserve(e.target);
					}
				}
			},
			{ threshold: 0.01, rootMargin: ROOT_MARGIN }
		);
		targets.forEach((el) => io.observe(el));
		return () => io.disconnect();
	}, [isStagger, delay]);

	return (
		<Tag ref={ref} className={`reveal${className ? " " + className : ""}`} {...rest}>
			{children}
		</Tag>
	);
}

export default Reveal;
